import styled from "@emotion/styled";
import { Menu, MenuItem, Divider as MuiDivider } from "@mui/material";
import { getFeedback } from "@sentry/react";
import { useCallback, useState, type MouseEvent } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { Button } from "../buttons/Buttons";
import { Tooltip } from "../dataDisplay";
import { Icon, SkiliticsDuotoneIcon } from "../icons";

type FeedbackDialog = Awaited<ReturnType<NonNullable<ReturnType<typeof getFeedback>>["createForm"]>>;

const helpCenterLink = "https://skilitics.com/help-center";
const releaseNotesLink = "https://skilitics.com/release-notes";

const iconSize = 14;

const StyledIconButton = styled(Button)`
  background: ${({ theme }) => theme.surface};
  font-size: 18px;
  line-height: 1;
  height: 32px;
  width: 32px;
  padding: 0;
  min-width: 0;
`;

const MenuIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  width: 20px;
`;

const Divider = styled(MuiDivider)`
  margin: 10px 0;
`;

const VersionMenuItem = styled(MenuItem)`
  cursor: default;

  &:hover,
  &.Mui-focusVisible {
    background-color: transparent;
  }
`;

type Props = {
  showKeyboardShortcuts: () => void;
  isTutorialsEnabled: boolean;
  toggleTutorials: () => void;
  appVersion: string;
};

export const HelpPanel = ({ showKeyboardShortcuts, isTutorialsEnabled, toggleTutorials, appVersion }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [feedbackDialog, setFeedbackDialog] = useState<FeedbackDialog | null>(null);

  const openPopover = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

  const closePopover = () => setAnchorEl(null);

  const showFeedbackDialog = useCallback(
    (feedbackOrBug: "feedback" | "bug") => {
      const feedback = getFeedback();

      if (!feedback) return;

      feedbackDialog?.removeFromDom();

      feedback
        .createForm({
          formTitle: formatMessage(feedbackOrBug),
          submitButtonLabel: formatMessage(`${feedbackOrBug}SubmitButton`),
          messagePlaceholder: formatMessage(`${feedbackOrBug}DescriptionPlaceholder`),
        })
        .then((form) => {
          setFeedbackDialog(form);
          form.appendToDom();
          form.open();
          closePopover();
        })
        .catch(console.warn);
    },
    [feedbackDialog, formatMessage],
  );

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip key="help-popover" title={formatMessage("tooltip")} placement="left">
        <StyledIconButton onClick={openPopover} variant="outlined" data-test="help-popover-button">
          ?
        </StyledIconButton>
      </Tooltip>

      <Menu
        id="help-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          key="keyboardShortcuts"
          onClick={showKeyboardShortcuts}
          data-test="header-settings-menu-item-keyboard-shortcuts"
        >
          <MenuIconContainer>
            <Icon icon="keyboard" size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage("keyboardShortcuts")}</span>
        </MenuItem>

        <MenuItem key="tutorials" onClick={toggleTutorials} data-test="header-settings-menu-item-tutorials">
          <MenuIconContainer>
            <SkiliticsDuotoneIcon icon={isTutorialsEnabled ? "switch-on" : "switch-off"} size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage(isTutorialsEnabled ? "disableTutorials" : "enableTutorials")}</span>
        </MenuItem>

        <MenuItem
          key="help-center"
          component="a"
          href={helpCenterLink}
          target="_blank"
          rel="noopener"
          onClick={closePopover}
          data-test="header-settings-menu-item-help-center"
        >
          <MenuIconContainer>
            <Icon icon="books" size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage("helpCenter")}</span>
        </MenuItem>

        <MenuItem
          key="release-notes"
          component="a"
          href={releaseNotesLink}
          target="_blank"
          rel="noopener"
          onClick={closePopover}
          data-test="header-settings-menu-item-release-notes"
        >
          <MenuIconContainer>
            <Icon icon="gift" size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage("releaseNotes")}</span>
        </MenuItem>

        <Divider />

        <MenuItem key="feedback" id="feedbackButton" onClick={() => showFeedbackDialog("feedback")}>
          <MenuIconContainer>
            <Icon icon="bullhorn" size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage("feedback")}</span>
        </MenuItem>

        <MenuItem key="bug" id="bugReportButton" onClick={() => showFeedbackDialog("bug")}>
          <MenuIconContainer>
            <Icon icon="flag" size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage("bug")}</span>
        </MenuItem>

        <Divider />

        <VersionMenuItem key="version" onClick={() => navigator.clipboard.writeText(appVersion)} disableRipple>
          <span>Version {appVersion}</span>
        </VersionMenuItem>

        {/* <Tooltip title={formatMessage("comingSoon")} placement="left">
          <span>
            <MenuItem key="community" id="communitySupportButton" disabled>
              <MenuIconContainer>
                <Icon icon="comments-question" />
              </MenuIconContainer>
              <span>{formatMessage("communitySupport")}</span>
            </MenuItem>
          </span>
        </Tooltip>

        <Tooltip title={formatMessage("comingSoon")} placement="left">
          <span>
            <MenuItem key="support" id="contactSupportButton" disabled>
              <MenuIconContainer>
                <Icon icon="headset" />
              </MenuIconContainer>
              <span>{formatMessage("contactSupport")}</span>
            </MenuItem>
          </span>
        </Tooltip> */}
      </Menu>
    </>
  );
};

const formatStrings = {
  comingSoon: {
    en: "Coming Soon",
  },
  tooltip: {
    en: "Help & Support Resources",
  },
  keyboardShortcuts: {
    en: "Keyboard Shortcuts",
  },
  enableTutorials: {
    en: "Enable Tutorials",
  },
  disableTutorials: {
    en: "Disable Tutorials",
  },
  helpCenter: {
    en: "Help Center",
  },
  releaseNotes: {
    en: "Release Notes",
  },
  feedback: {
    en: "Submit Feedback",
  },
  feedbackSubmitButton: {
    en: "Send Feedback",
  },
  feedbackDescriptionPlaceholder: {
    en: "Enter your feedback here",
  },
  bug: {
    en: "Report an Issue",
  },
  bugSubmitButton: {
    en: "Send Bug Report",
  },
  bugDescriptionPlaceholder: {
    en: "What's the bug? What did you expect?",
  },
  communitySupport: {
    en: "Community Support",
  },
  contactSupport: {
    en: "Contact Support",
  },
} satisfies FormatMessages;
